import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Share2, Copy, Check, Mail, Linkedin, Twitter } from "lucide-react";

interface ShareDialogProps {
  newsId: number;
  title: string;
  variant?: "default" | "ghost" | "outline";
  size?: "default" | "sm" | "lg" | "icon";
  showLabel?: boolean;
}

export function ShareDialog({
  newsId,
  title,
  variant = "ghost",
  size = "sm",
  showLabel = false,
}: ShareDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { toast } = useToast();

  const shareUrl = `${window.location.origin}/news/${newsId}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      toast({
        title: "Link copied!",
        description: "The link has been copied to your clipboard.",
      });
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy link. Please try again.",
        variant: "destructive",
      });
    }
  };

  const shareLinks = [
    {
      name: "Email",
      icon: Mail,
      url: `mailto:?subject=${encodedTitle}&body=Check out this news: ${encodedUrl}`,
      color: "bg-slate-600 hover:bg-slate-700",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "bg-[#0A66C2] hover:bg-[#094c91]",
    },
    {
      name: "Twitter",
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      color: "bg-[#1DA1F2] hover:bg-[#0c85d0]",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className="gap-2">
          <Share2 className="h-4 w-4" />
          {showLabel && <span>Share</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share this news</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Copy link */}
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={shareUrl}
              className="flex-1 text-sm"
            />
            <Button
              size="sm"
              onClick={handleCopyLink}
              className="gap-2 min-w-[80px]"
            >
              {isCopied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </Button>
          </div>

          {/* Share buttons */}
          <div className="flex justify-center gap-3">
            {shareLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-center w-12 h-12 rounded-full text-white transition-colors ${link.color}`}
                title={`Share on ${link.name}`}
              >
                <link.icon className="h-5 w-5" />
              </a>
            ))}
          </div>

          {/* Native share (if supported) */}
          {typeof navigator.share === "function" && (
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => {
                navigator.share({
                  title: title,
                  url: shareUrl,
                }).catch(() => {
                  // User cancelled or error
                });
              }}
            >
              <Share2 className="h-4 w-4" />
              More sharing options
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
